<script setup>
import { reactive, onMounted, ref, defineProps, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useInbox } from 'dashboard/composables/useInbox';
import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';

// GK-HUB: CSAT WhatsApp inline buttons — removed template + survey rule imports
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import CSATDisplayTypeSelector from './components/CSATDisplayTypeSelector.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');

const { isAWhatsAppChannel, isATwilioWhatsAppChannel } = useInbox(
  props.inbox?.id
);

// Computed to check if it's any type of WhatsApp channel (Cloud or Twilio)
const isAnyWhatsAppChannel = computed(
  () => isAWhatsAppChannel.value || isATwilioWhatsAppChannel.value
);

const isUpdating = ref(false);
const selectedLabelValues = ref([]);

const state = reactive({
  csatSurveyEnabled: false,
  displayType: 'emoji',
  message: '',
  templateButtonText: 'Please rate us',
  surveyRuleOperator: 'contains',
  templateLanguage: '',
  responseWindowMinutes: 10, // GK-HUB: CSAT response window setting
  thankYouPoor: '', // GK-HUB: CSAT custom thank you messages
  thankYouNeutral: '',
  thankYouGreat: '',
  feedbackEnabled: false, // GK-HUB: CSAT feedback option
  feedbackMessage: '',
  closingMessage: '', // GK-HUB: CSAT closing/timeout messages
  timeoutMessage: '',
  csatFormat: 'buttons_3', // GK-HUB: CSAT format (buttons_3 or list_5)
});

// GK-HUB: Removed unused refs/vars (templateStatus, templateLoading, confirmDialog, originalTemplateValues, filterTypes, currentLabel)

// GK-HUB: Removed unused computed (labelOptions, languageOptions, messagePreviewData, shouldShowTemplateStatus, templateApprovalStatus)

const initializeState = () => {
  if (!props.inbox) return;

  const { csat_survey_enabled, csat_config } = props.inbox;

  state.csatSurveyEnabled = csat_survey_enabled || false;

  if (!csat_config) return;

  const {
    display_type: displayType = CSAT_DISPLAY_TYPES.EMOJI,
    message = '',
    button_text: buttonText = 'Please rate us',
    language = 'en',
    survey_rules: surveyRules = {},
  } = csat_config;

  state.displayType = displayType;
  state.message = message;
  state.templateButtonText = buttonText;
  state.templateLanguage = language;
  state.surveyRuleOperator = surveyRules.operator || 'contains';
  // GK-HUB: CSAT response window setting
  state.responseWindowMinutes = csat_config.response_window_minutes || 10;
  // GK-HUB: CSAT custom thank you messages
  state.thankYouPoor = csat_config.thank_you_poor || '';
  state.thankYouNeutral = csat_config.thank_you_neutral || '';
  state.thankYouGreat = csat_config.thank_you_great || '';
  // GK-HUB: CSAT feedback option
  state.feedbackEnabled = csat_config.feedback_enabled || false;
  state.feedbackMessage = csat_config.feedback_message || '';
  // GK-HUB: CSAT closing/timeout messages
  state.closingMessage = csat_config.closing_message || '';
  state.timeoutMessage = csat_config.timeout_message || '';
  // GK-HUB: CSAT format
  state.csatFormat = csat_config.csat_format || 'buttons_3';

  selectedLabelValues.value = Array.isArray(surveyRules.values)
    ? [...surveyRules.values]
    : [];
};

// GK-HUB: checkTemplateStatus removed — inline buttons don't use templates

onMounted(() => {
  initializeState();
  if (!labels.value?.length) store.dispatch('labels/get');
});

watch(() => props.inbox, initializeState, { immediate: true });

// GK-HUB: Removed unused functions (handleLabelSelect, updateSurveyRuleOperator, removeLabel)

const updateDisplayType = type => {
  state.displayType = type;
};

// GK-HUB: Template helper functions removed (hasTemplateChanges, hasExistingTemplate, shouldCreateTemplate, buildTemplateConfig)

const updateInbox = async attributes => {
  const payload = {
    id: props.inbox.id,
    formData: false,
    ...attributes,
  };

  await store.dispatch('inboxes/updateInbox', payload);
};

// GK-HUB: CSAT WhatsApp inline buttons — simplified save (no template creation)
const performSave = async () => {
  try {
    isUpdating.value = true;

    const csatConfig = {
      display_type: state.displayType,
      message: state.message,
      response_window_minutes: state.responseWindowMinutes,
      thank_you_poor: state.thankYouPoor,
      thank_you_neutral: state.thankYouNeutral,
      thank_you_great: state.thankYouGreat,
      feedback_enabled: state.feedbackEnabled,
      feedback_message: state.feedbackMessage,
      closing_message: state.closingMessage,
      timeout_message: state.timeoutMessage,
      csat_format: state.csatFormat,
      survey_rules: {
        operator: state.surveyRuleOperator,
        values: selectedLabelValues.value,
      },
    };

    await updateInbox({
      csat_survey_enabled: state.csatSurveyEnabled,
      csat_config: csatConfig,
    });

    useAlert(t('INBOX_MGMT.CSAT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.CSAT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
// GK-HUB: end CSAT WhatsApp inline buttons

// GK-HUB: CSAT WhatsApp inline buttons — direct save, no template confirmation
const saveSettings = async () => {
  await performSave();
};
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('INBOX_MGMT.CSAT.TITLE')"
      :description="$t('INBOX_MGMT.CSAT.SUBTITLE')"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="state.csatSurveyEnabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <!-- Show display type only for non-WhatsApp channels -->
        <WithLabel
          v-if="!isAnyWhatsAppChannel"
          :label="$t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL')"
          name="display_type"
        >
          <CSATDisplayTypeSelector
            :selected-type="state.displayType"
            @update="updateDisplayType"
          />
        </WithLabel>

        <!-- GK-HUB: CSAT WhatsApp inline buttons — simplified settings -->
        <template v-if="isAnyWhatsAppChannel">
          <div class="flex flex-col gap-4">
            <Input
              v-model="state.message"
              :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')"
              :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
              class="w-full"
            />

            <!-- GK-HUB: CSAT format selector + preview -->
            <div
              class="p-4 rounded-xl bg-n-slate-2 outline outline-1 outline-n-weak"
            >
              <p
                class="mb-3 text-xs font-medium tracking-wide uppercase text-n-slate-10"
              >
                {{ $t('INBOX_MGMT.CSAT.FORMAT.LABEL') }}
              </p>
              <div class="flex gap-3 mb-4">
                <button
                  class="flex-1 py-2.5 text-sm font-medium rounded-lg border transition-all"
                  :class="
                    state.csatFormat === 'buttons_3'
                      ? 'text-n-brand border-n-brand bg-n-alpha-black2'
                      : 'text-n-slate-11 border-n-weak hover:border-n-slate-6'
                  "
                  @click="state.csatFormat = 'buttons_3'"
                >
                  {{ $t('INBOX_MGMT.CSAT.FORMAT.BUTTONS_3') }}
                </button>
                <button
                  class="flex-1 py-2.5 text-sm font-medium rounded-lg border transition-all"
                  :class="
                    state.csatFormat === 'list_5'
                      ? 'text-n-brand border-n-brand bg-n-alpha-black2'
                      : 'text-n-slate-11 border-n-weak hover:border-n-slate-6'
                  "
                  @click="state.csatFormat = 'list_5'"
                >
                  {{ $t('INBOX_MGMT.CSAT.FORMAT.LIST_5') }}
                </button>
              </div>

              <!-- Preview -->
              <p
                class="mb-2 text-xs font-medium tracking-wide uppercase text-n-slate-10"
              >
                {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.LABEL') }}
              </p>
              <div
                class="flex flex-col gap-2 p-4 mx-auto max-w-xs rounded-lg bg-n-solid-3"
              >
                <p class="text-xs font-semibold text-n-brand">
                  {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.SENDER') }}
                </p>
                <p class="text-sm text-n-slate-12">
                  {{
                    state.message || $t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')
                  }}
                </p>
                <!-- 3 buttons preview -->
                <div
                  v-if="state.csatFormat === 'buttons_3'"
                  class="flex flex-col gap-1.5 mt-2"
                >
                  <div
                    class="py-2 text-sm font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.BTN_POOR') }}
                  </div>
                  <div
                    class="py-2 text-sm font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.BTN_NEUTRAL') }}
                  </div>
                  <div
                    class="py-2 text-sm font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.BTN_GREAT') }}
                  </div>
                </div>
                <!-- 5 list preview -->
                <div v-else class="flex flex-col gap-1 mt-2">
                  <div
                    class="py-1.5 text-xs font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.LIST_1') }}
                  </div>
                  <div
                    class="py-1.5 text-xs font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.LIST_2') }}
                  </div>
                  <div
                    class="py-1.5 text-xs font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.LIST_3') }}
                  </div>
                  <div
                    class="py-1.5 text-xs font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.LIST_4') }}
                  </div>
                  <div
                    class="py-1.5 text-xs font-medium text-center rounded-lg border text-n-brand border-n-weak"
                  >
                    {{ $t('INBOX_MGMT.CSAT.INLINE_PREVIEW.LIST_5') }}
                  </div>
                </div>
              </div>
            </div>
            <!-- GK-HUB: end CSAT format selector -->
          </div>

          <!-- GK-HUB: CSAT response messages -->
          <div
            class="flex flex-col gap-5 p-5 rounded-xl bg-n-slate-2 outline outline-1 outline-n-weak"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.CSAT.MESSAGES.TITLE') }}
            </p>
            <p class="-mt-3 text-xs text-n-slate-10">
              {{ $t('INBOX_MGMT.CSAT.MESSAGES.SUBTITLE') }}
            </p>
            <Input
              v-model="state.thankYouPoor"
              :label="$t('INBOX_MGMT.CSAT.THANK_YOU.POOR')"
              :placeholder="$t('INBOX_MGMT.CSAT.THANK_YOU.POOR_PLACEHOLDER')"
              class="w-full"
            />
            <Input
              v-model="state.thankYouNeutral"
              :label="$t('INBOX_MGMT.CSAT.THANK_YOU.NEUTRAL')"
              :placeholder="$t('INBOX_MGMT.CSAT.THANK_YOU.NEUTRAL_PLACEHOLDER')"
              class="w-full"
            />
            <Input
              v-model="state.thankYouGreat"
              :label="$t('INBOX_MGMT.CSAT.THANK_YOU.GREAT')"
              :placeholder="$t('INBOX_MGMT.CSAT.THANK_YOU.GREAT_PLACEHOLDER')"
              class="w-full"
            />
            <Input
              v-model="state.closingMessage"
              :label="$t('INBOX_MGMT.CSAT.CLOSING.LABEL')"
              :placeholder="$t('INBOX_MGMT.CSAT.CLOSING.PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- GK-HUB: CSAT feedback option -->
          <div
            class="flex flex-col gap-4 p-4 rounded-xl bg-n-slate-2 outline outline-1 outline-n-weak"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.CSAT.FEEDBACK.TOGGLE_LABEL') }}
                </p>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ $t('INBOX_MGMT.CSAT.FEEDBACK.TOGGLE_HELP') }}
                </p>
              </div>
              <Switch v-model="state.feedbackEnabled" />
            </div>
            <Input
              v-if="state.feedbackEnabled"
              v-model="state.feedbackMessage"
              :label="$t('INBOX_MGMT.CSAT.FEEDBACK.MESSAGE_LABEL')"
              :placeholder="$t('INBOX_MGMT.CSAT.FEEDBACK.MESSAGE_PLACEHOLDER')"
              class="w-full"
            />
          </div>
          <!-- GK-HUB: end CSAT feedback + closing -->

          <!-- GK-HUB: CSAT timeout settings (grouped in card) -->
          <div
            class="flex flex-col gap-4 p-5 rounded-xl bg-n-slate-2 outline outline-1 outline-n-weak"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.CSAT.TIMEOUT_SECTION.TITLE') }}
            </p>
            <p class="-mt-3 text-xs text-n-slate-10">
              {{ $t('INBOX_MGMT.CSAT.TIMEOUT_SECTION.SUBTITLE') }}
            </p>
            <Input
              v-model.number="state.responseWindowMinutes"
              :label="$t('INBOX_MGMT.CSAT.RESPONSE_WINDOW.LABEL')"
              type="number"
              min="1"
              max="1440"
              class="w-32"
            />
            <Input
              v-model="state.timeoutMessage"
              :label="$t('INBOX_MGMT.CSAT.TIMEOUT_MSG.LABEL')"
              :placeholder="$t('INBOX_MGMT.CSAT.TIMEOUT_MSG.PLACEHOLDER')"
              class="w-full"
            />
          </div>
          <!-- GK-HUB: end CSAT timeout settings -->
        </template>
        <!-- GK-HUB: end CSAT WhatsApp inline buttons -->

        <!-- Non-WhatsApp channels layout -->
        <template v-else>
          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')"
            name="message"
          >
            <Editor
              v-model="state.message"
              :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
              :max-length="200"
              class="w-full"
            />
          </WithLabel>
        </template>

        <p class="text-sm italic text-n-slate-11">
          {{
            isAnyWhatsAppChannel
              ? $t('INBOX_MGMT.CSAT.WHATSAPP_INLINE_NOTE')
              : $t('INBOX_MGMT.CSAT.NOTE')
          }}
        </p>
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>

    <!-- GK-HUB: Template dialog removed — inline buttons don't use templates -->
  </div>
</template>
